import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import styled from "styled-components";
import { MdCampaign, MdEdit, MdList, MdBarChart, MdInfoOutline } from 'react-icons/md';
import Chart from "react-apexcharts";

import { fetchCampaign, updateCampaign, calculateCampaign } from "../../../services/Http/Api";
import PromotionList from "./PromotionList";
import PromotionEdit from "./PromotionEdit";
import Campaign from "../../../models/global/DataModels/Campaign/Campaign";
import PromotionOverlay from "../../../models/global/DataModels/Campaign/Promotion/PromotionOverlay";
import Promotion from "../../../models/global/DataModels/Campaign/Promotion/Promotion";

import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import TabBar from '../../../shared/layout/tabs/TabBar';
import TabButton from '../../../shared/layout/tabs/TabButton';
import MainButton from '../../../shared/layout/buttons/MainButton';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import { Section, Grid } from '../../../shared/layout/containers/Section';
import {
    StyledInput,
    StyledTextArea,
    StyledLabel,
    FormGroup,
    FormFooter
} from '../../../shared/layout/forms/FormElements';
import { useCurrency } from '../../../context/CurrencyProvider';

const CampaignEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { formatEurAmount } = useCurrency();
    const campaignRef = useRef({ name: "Initial Campaign" });
    const [selectedPromotionIndex, setSelectedPromotionIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [campaignJson, setCampaignJson] = useState(null);
    const [activeTab, setActiveTab] = useState('edit');
    const [, forceUpdate] = useState(0);

    useEffect(() => {
        const loadCampaign = async () => {
            try {
                const campaignData = await fetchCampaign(id);
                campaignRef.current = new Campaign(campaignData);
            } catch (error) {
                console.error('Error loading campaign:', error);
                toast.error(t('Campaigns.CampaignEdit.failedLoad', { defaultValue: 'Failed to load campaign.' }));
            } finally {
                setLoading(false);
            }
        };

        loadCampaign();
    }, [id]);

    const handleInputChange = (field, value) => {
        if (campaignRef.current) {
            campaignRef.current[field] = value;
            forceUpdate((prev) => prev + 1);
        }
    };

    const handlePromotionSelection = (index) => {
        setSelectedPromotionIndex(index);
    };

    const handleAddPromotion = () => {
        if (campaignRef.current) {
            const newPromotion = new Promotion({
                id: null,
                name: 'New Promotion',
                description: '',
                startDate: new Date().toISOString().split('T')[0],
                endDate: new Date(new Date().setDate(new Date().getDate() + 7)).toISOString().split('T')[0],
                daysOfWeek: [],
                startTime: '09:00:00',
                endTime: '17:00:00',
                discount: 0,
                pointsMultiplier: 0,
                active: true,
                conditions: [],
                targets: [],
                incentives: [],
                strategies: [],
                promotionOverlay: new PromotionOverlay({}),
            });
            campaignRef.current.promotions.push(newPromotion);
            setSelectedPromotionIndex(campaignRef.current.promotions.length - 1);
            forceUpdate((prev) => prev + 1);
        }
    };

    const handlePromotionChange = (index, field, value) => {
        if (campaignRef.current) {
            const updatedPromotion = {
                ...campaignRef.current.promotions[index],
                [field]: value,
            };
            campaignRef.current.promotions[index] = updatedPromotion;
        }
    };

    const handleDeletePromotion = (index) => {
        if (campaignRef.current) {
            campaignRef.current.promotions.splice(index, 1);
            forceUpdate((prev) => prev + 1);
        }
    };

    const handleUpdateCampaign = async () => {
        setUpdating(true);
        try {
            await updateCampaign(id, campaignRef.current.toBackendFormat());
            toast.success(t('Campaigns.CampaignEdit.successUpdate'));
        } catch (error) {
            console.error('Error updating campaign:', error);
            toast.error(t('Campaigns.CampaignEdit.failedUpdate'));
        } finally {
            setUpdating(false);
        }
    };

    const handleCalculate = async () => {
        try {
            const calculatedData = await calculateCampaign(campaignRef.current);
            setCampaignJson(calculatedData);
        } catch (error) {
            console.error('Error calculating campaign:', error);
            toast.error(t('Campaigns.CampaignEdit.failedCalculation'));
        }
    };

    if (loading) return <LoadingMessage>{t('common.loading') || 'Loading...'}</LoadingMessage>;

    const revenueChartOptions = {
        chart: { type: 'bar', height: 350 },
        plotOptions: { bar: { horizontal: false, columnWidth: '55%', endingShape: 'rounded' } },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        xaxis: { categories: ['Total Budget', 'Total Reach'] },
        yaxis: { title: { text: 'Value' } },
        fill: { opacity: 1 },
        colors: ['#821692', '#00E396'],
    };

    const revenueChartSeries = [{
        name: 'Metrics',
        data: campaignJson ? [campaignJson.totalBudget, campaignJson.totalReach] : [0, 0]
    }];

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdCampaign size={28} />}>{t('Campaigns.CampaignEdit.editCampaignTitle')}</PageTitle>
            </PageHeader>
            <TabBar>
                <TabButton
                    active={activeTab === 'edit'}
                    onClick={() => setActiveTab('edit')}
                    icon={<MdEdit />}
                >
                    {t('Campaigns.CampaignEdit.editTab')}
                </TabButton>
                <TabButton
                    active={activeTab === 'promotions'}
                    onClick={() => setActiveTab('promotions')}
                    icon={<MdList />}
                >
                    {t('Campaigns.CampaignEdit.promotionsTab')}
                </TabButton>
                <TabButton
                    active={activeTab === 'analysis'}
                    onClick={() => setActiveTab('analysis')}
                    icon={<MdBarChart />}
                >
                    {t('Campaigns.CampaignEdit.analysisTab')}
                </TabButton>
            </TabBar>
            <PageContent>
                {activeTab === 'edit' && (
                    <StyledCard>
                        <Form>
                            <SectionTitle style={{ marginBottom: '24px' }}>
                                <MdInfoOutline size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                {t('Campaigns.CampaignEdit.generalDetails') || 'General Details'}
                            </SectionTitle>

                            <FormGroup>
                                <StyledLabel htmlFor="campaignname">{t('Campaigns.name')}</StyledLabel>
                                <StyledInput
                                    type="text"
                                    id="campaignname"
                                    value={campaignRef.current?.name || ''}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder={t('Campaigns.CampaignEdit.placeholderName')}
                                />
                            </FormGroup>

                            <FormGroup>
                                <StyledLabel htmlFor="campaigndescription">{t('Campaigns.description')}</StyledLabel>
                                <StyledTextArea
                                    id="campaigndescription"
                                    value={campaignRef.current?.description || ''}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    placeholder={t('Campaigns.CampaignEdit.placeholderDescription')}
                                    rows={3}
                                />
                            </FormGroup>

                            <FormGroup>
                                <StyledLabel htmlFor="campaigngoalDescription">{t('Campaigns.goalDescription')}</StyledLabel>
                                <StyledTextArea
                                    id="campaigngoalDescription"
                                    value={campaignRef.current?.goalDescription || ''}
                                    onChange={(e) => handleInputChange('goalDescription', e.target.value)}
                                    placeholder={t('Campaigns.CampaignEdit.placeholderGoalDescription')}
                                    rows={3}
                                />
                            </FormGroup>

                            <SectionTitle style={{ marginTop: '16px', marginBottom: '24px' }}>
                                <MdBarChart size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                {t('Campaigns.CampaignEdit.timelineAndBudget') || 'Timeline & Budget'}
                            </SectionTitle>

                            <Grid columns={3} gap="24px">
                                <FormGroup>
                                    <StyledLabel htmlFor="campaignbudget">{t('Campaigns.budget')}</StyledLabel>
                                    <StyledInput
                                        type="number"
                                        id="campaignbudget"
                                        value={campaignRef.current?.budget || ''}
                                        onChange={(e) => handleInputChange('budget', e.target.value)}
                                        placeholder={t('Campaigns.CampaignEdit.placeholderBudget')}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <StyledLabel htmlFor="campaignstartDate">{t('Campaigns.startDate')}</StyledLabel>
                                    <StyledInput
                                        type="date"
                                        id="campaignstartDate"
                                        value={campaignRef.current?.startDate || ''}
                                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <StyledLabel htmlFor="campaignendDate">{t('Campaigns.endDate')}</StyledLabel>
                                    <StyledInput
                                        type="date"
                                        id="campaignendDate"
                                        value={campaignRef.current?.endDate || ''}
                                        onChange={(e) => handleInputChange('endDate', e.target.value)}
                                    />
                                </FormGroup>
                            </Grid>

                            <FormFooter style={{ marginTop: '32px' }}>
                                <MainButton variant="secondary" onClick={() => navigate('/campaigns')}>
                                    {t('Campaigns.CampaignEdit.backToCampaigns', { defaultValue: 'Back to campaigns' })}
                                </MainButton>
                                <MainButton onClick={handleUpdateCampaign} disabled={updating}>
                                    {updating ? t('Campaigns.saving') : t('Campaigns.CampaignEdit.updateCampaign')}
                                </MainButton>
                            </FormFooter>
                        </Form>
                    </StyledCard>
                )}
                {activeTab === 'promotions' && (
                    <StyledCard>
                        <PromotionList
                            campaignRef={campaignRef}
                            selectedPromotionIndex={selectedPromotionIndex}
                            onSelectPromotion={handlePromotionSelection}
                            onAddPromotion={handleAddPromotion}
                            onDeletePromotion={handleDeletePromotion}
                        />
                        <div style={{ marginTop: '24px', borderTop: '1px solid #f3f4f6', paddingTop: '24px' }}>
                            {campaignRef.current.promotions.length > 0 && (
                                <PromotionEdit
                                    campaignRef={campaignRef}
                                    key={selectedPromotionIndex}
                                    selectedPromotionIndex={selectedPromotionIndex}
                                    onUpdatePromotion={(field, value) => handlePromotionChange(field, value)}
                                />
                            )}
                        </div>
                    </StyledCard>
                )}
                {activeTab === 'analysis' && (
                    <StyledCard>
                        <AnalysisHeader>
                            <h4>{t('Campaigns.CampaignEdit.calculations')}</h4>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <MainButton variant="secondary" onClick={handleCalculate} disabled={updating} icon={<MdBarChart />}>
                                    {updating ? t('Campaigns.calculating') : t('Campaigns.CampaignEdit.calculate')}
                                </MainButton>
                                <MainButton onClick={handleUpdateCampaign} disabled={updating}>
                                    {updating ? t('Campaigns.saving') : t('Campaigns.CampaignEdit.updateCampaign')}
                                </MainButton>
                            </div>
                        </AnalysisHeader>

                        {campaignJson ? (
                            <ResultsContainer>
                                <Grid columns={2} gap="1.5rem">
                                    <StatCard>
                                        <StatIcon><MdBarChart size={24} /></StatIcon>
                                        <div style={{ textAlign: 'center' }}>
                                            <StatLabel>{t('Campaigns.CampaignEdit.totalBudget', { defaultValue: 'Total Budget' })}</StatLabel>
                                            <StatValue>{campaignJson.totalBudget != null ? formatEurAmount(campaignJson.totalBudget) : '-'}</StatValue>
                                        </div>
                                    </StatCard>
                                    <StatCard>
                                        <StatIcon style={{ backgroundColor: '#e6fffa', color: '#00B8D9' }}><MdList size={24} /></StatIcon>
                                        <div style={{ textAlign: 'center' }}>
                                            <StatLabel>{t('Campaigns.CampaignEdit.reach')}</StatLabel>
                                            <StatValue>{campaignJson.totalReach || '-'}</StatValue>
                                        </div>
                                    </StatCard>
                                </Grid>

                                <ChartWrapper>
                                    <Chart options={revenueChartOptions} series={revenueChartSeries} type="bar" height={350} />
                                </ChartWrapper>

                                <AnalysisContentGrid>
                                    <Section>
                                        <SectionTitle>{t('Campaigns.CampaignEdit.reaches')}</SectionTitle>
                                        <AnalysisList>
                                            {campaignJson.reaches.map((promotionReach, promotionIndex) => (
                                                <AnalysisListItem key={`reach_${promotionIndex}`}>
                                                    <h6>{t('Campaigns.CampaignEdit.promotion')} {promotionIndex + 1}</h6>
                                                    <ul>
                                                        <li>{t('Campaigns.CampaignEdit.initialReach')}: <strong>{promotionReach.initialReach}</strong></li>
                                                        {promotionReach.targets.map((target, targetIndex) => (
                                                            <li key={`target_reach_${targetIndex}`}>
                                                                {t('Campaigns.CampaignEdit.target')} {targetIndex + 1}: <strong>{target.reach}</strong>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </AnalysisListItem>
                                            ))}
                                        </AnalysisList>
                                    </Section>

                                    <Section>
                                        <SectionTitle>{t('Campaigns.CampaignEdit.prices')}</SectionTitle>
                                        <AnalysisList>
                                            {campaignJson.prices.map((promotionPrice, promotionIndex) => (
                                                <AnalysisListItem key={`price_${promotionIndex}`}>
                                                    <h6>{t('Campaigns.CampaignEdit.promotion')} {promotionIndex + 1}</h6>
                                                    <ul>
                                                        {promotionPrice.strategies.map((strategy, strategyIndex) => (
                                                            <li key={`strategy_price_${strategyIndex}`}>
                                                                {t('Campaigns.CampaignEdit.strategy')} {strategyIndex + 1}: <strong>{formatEurAmount(strategy.price)}</strong>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </AnalysisListItem>
                                            ))}
                                        </AnalysisList>
                                    </Section>

                                    <Section style={{ gridColumn: 'span 2' }}>
                                        <SectionTitle>{t('Campaigns.CampaignEdit.suggestions')}</SectionTitle>
                                        <AnalysisList $columns={2}>
                                            {campaignJson.suggestions.map((promotionSuggestions, promotionIndex) => (
                                                <AnalysisListItem key={`suggestions_${promotionIndex}`}>
                                                    <h6>{t('Campaigns.CampaignEdit.promotion')} {promotionIndex + 1}</h6>
                                                    <ul>
                                                        {promotionSuggestions.length > 0 ? (
                                                            promotionSuggestions.map((suggestion, suggestionIndex) => (
                                                                <li key={`suggestion_${suggestionIndex}`}>{suggestion}</li>
                                                            ))
                                                        ) : (
                                                            <li>{t('Campaigns.CampaignEdit.noSuggestions')}</li>
                                                        )}
                                                    </ul>
                                                </AnalysisListItem>
                                            ))}
                                        </AnalysisList>
                                    </Section>
                                </AnalysisContentGrid>
                            </ResultsContainer>
                        ) : (
                            <div style={{ padding: '2.5rem', textAlign: 'center', color: '#6b7280' }}>
                                {t('Campaigns.CampaignEdit.clickCalculatePlaceholder', {
                                    defaultValue: 'Click "Calculate" to see analysis results.'
                                })}
                            </div>
                        )}
                    </StyledCard>
                )}
            </PageContent>
        </PageContainer>
    );
};

export default CampaignEdit;

const Form = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
`;

const AnalysisHeader = styled.div`
    margin-bottom: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    h4 {
        margin: 0;
        color: #111827;
        font-size: 1.25rem;
        font-weight: 600;
    }
`;

const StatCard = styled.div`
    background: #ffffff;
    padding: 1.5rem;
    border-radius: 16px;
    border: 1px solid #eef0f2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    transition: transform 0.2s ease;

    &:hover {
        transform: translateY(-2px);
    }
`;

const StatIcon = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background-color: #fcefff;
    color: #821692;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.25rem;
`;

const StatLabel = styled.span`
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    display: block;
    margin-bottom: 0.25rem;
`;

const StatValue = styled.span`
    font-size: 1.75rem;
    color: #111827;
    font-weight: 800;
`;

const ChartWrapper = styled.div`
    background: #ffffff;
    padding: 1.5rem;
    border-radius: 16px;
    border: 1px solid #eef0f2;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
`;

const ResultsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2rem;
`;

const AnalysisContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

const SectionTitle = styled.h6`
    font-weight: 700;
    color: #821692;
    margin: 0 0 1rem 0;
    font-size: 1rem;
    display: flex;
    align-items: center;
    letter-spacing: 0.01em;

    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #f3e8f4;
        margin-left: 1rem;
    }
`;

const AnalysisList = styled.div`
    display: grid;
    grid-template-columns: ${({ $columns }) => ($columns ? `repeat(${$columns}, 1fr)` : '1fr')};
    gap: 1rem;
`;

const AnalysisListItem = styled.div`
    background: #f9fafb;
    padding: 1rem;
    border-radius: 12px;
    border: 1px solid #f3f4f6;

    h6 {
        margin: 0 0 0.75rem 0;
        font-size: 0.875rem;
        font-weight: 700;
        color: #374151;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    ul {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        li {
            font-size: 0.875rem;
            color: #4b5563;
            display: flex;
            justify-content: space-between;
            
            strong {
                color: #111827;
            }
        }
    }
`;
